/**
 * useUpsertGoal — PUT /v1/clinics/{clinic_id}/measures/{measure_id}/goal
 *
 * Records a goal commitment for a specific measure. Per the handoff §2,
 * goals are append-only: each PUT inserts a new row, and the dashboard
 * shows the latest via `measures[].current_goal`.
 *
 * Per "lazy meeting creation": if no meeting exists for the clinic-year,
 * the backend auto-creates one at the current quarter. No prerequisite UI
 * step is needed.
 *
 * The page wires this with a debounced-on-blur trigger from the Goal input
 * cell in the per-measure grid.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getApiClient } from "@/api/client";
import type { GoalResponse, GoalUpsertRequest } from "@/api/models";
import { queryKeys } from "./queryKeys";

export interface UpsertGoalVariables {
  measureId: number;
  /** Performance year — query param. */
  year: number;
  body: GoalUpsertRequest;
}

export function useUpsertGoal(clinicId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      measureId,
      year,
      body,
    }: UpsertGoalVariables): Promise<GoalResponse> => {
      const { data } = await getApiClient().put<GoalResponse>(
        `/v1/clinics/${clinicId}/measures/${measureId}/goal`,
        body,
        { params: { performance_year: year } },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.byClinic(clinicId) });
    },
  });
}
